// src/components/TeachingPlanCard/CollapsedPlanView.js
import React from "react";
import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditNoteIcon from "@mui/icons-material/EditNote";
import JourneyOverview2D from "./JourneyOverview2D";
import { PLAN_COLUMNS } from "../../utils/teachingPlan";
import { COL_WIDTHS } from "../plan/TeachingPlanEditor";
import { FW_FG } from "../plan/frameworkColors";
import { mluCodeOf } from "../../utils/teachingPlan";

const PREVIEW_ROWS = 3;

const clamp = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

/**
 * PlanPreview: the first sessions of the teaching plan, read-only and fading out,
 * so it is clear there is a full plan behind the journey map.
 */
function PlanPreview({ rows, onExpand, label }) {
  const compact = useMediaQuery(useTheme().breakpoints.down("sm"));
  return (
    <Box sx={{ position: "relative", mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Teaching plan
      </Typography>
      <Box
        onClick={onExpand}
        sx={{ position: "relative", maxHeight: 230, overflow: "hidden", cursor: "pointer" }}
        aria-hidden="true"
      >
        {compact ? (
          <Stack spacing={1}>
            {rows.slice(0, PREVIEW_ROWS).map((r, i) => (
              <Box key={i} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1, borderLeft: "4px solid",
                borderLeftColor: FW_FG[mluCodeOf(r.mlu)?.slice(0, 3)] || "grey.400" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                  {[r.session, r.mlu].filter(Boolean).join(" · ")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ...clamp(2), overflowWrap: "anywhere" }}>
                  {r.structure || r.methods}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
        <table style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              {PLAN_COLUMNS.map((c) => (
                <th key={c.key} style={{ width: COL_WIDTHS[c.key], textAlign: "left" }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, PREVIEW_ROWS).map((r, i) => (
              <tr key={i}>
                {PLAN_COLUMNS.map((c) => (
                  <td key={c.key}>
                    <Box sx={{ ...clamp(c.key === "structure" ? 3 : 2), color: "text.secondary" }}>{r[c.key]}</Box>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        )}
        {/* Fade into the card so the preview reads as "there is more" */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            background: (t) => `linear-gradient(to bottom, rgba(255,255,255,0), ${t.palette.background.paper})`,
          }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: -2.5, position: "relative" }}>
        <Button
          variant="contained"
          onClick={onExpand}
          startIcon={<EditNoteIcon />}
          endIcon={<ExpandMoreIcon />}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, boxShadow: 2 }}
        >
          {label}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * CollapsedPlanView
 * - JourneyOverview2D on top
 * - Below the map: a short preview of the teaching plan with one clear button to open it
 */
export default function CollapsedPlanView({ mluItems, rows, canEdit, onExpand }) {
  const label = canEdit ? "Show / edit teaching plan" : "Show full teaching plan";

  return (
    <>
      {/* 2D semantic journey overview */}
      <JourneyOverview2D items={mluItems} />

      {(rows || []).length > 0 ? (
        <PlanPreview rows={rows} onExpand={onExpand} label={label} />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button variant="contained" onClick={onExpand} endIcon={<ExpandMoreIcon />}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
            {label}
          </Button>
        </Box>
      )}
    </>
  );
}
